import React, { useState, useEffect } from 'react';
import { Play, Image as ImageIcon, X } from 'lucide-react';
import { getGallery } from '../lib/api';

// Default static gallery items (placeholders until admin uploads real content)
const DEFAULT_GALLERY = [
  {
    id: 'default-1',
    type: 'photo',
    title: 'Entrenamiento juvenil',
    description: 'Jóvenes desarrollando habilidades en cancha',
    data_url:
      'https://images.unsplash.com/photo-1526232761682-d26e03ac148e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
  },
  {
    id: 'default-2',
    type: 'photo',
    title: 'Trabajo en equipo',
    description: 'Coordinación y compañerismo en cada partido',
    data_url:
      'https://images.unsplash.com/photo-1600077063877-22118d6290eb?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
  },
  {
    id: 'default-3',
    type: 'photo',
    title: 'Hockey femenino',
    description: 'Atletas del interior alcanzando la elite',
    data_url:
      'https://images.unsplash.com/photo-1770669564911-bc1202acb626?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
  },
  {
    id: 'default-4',
    type: 'photo',
    title: 'Celebrando logros',
    description: 'El podio: meta cumplida',
    data_url:
      'https://images.unsplash.com/photo-1763639700421-070686cd1b81?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
  },
  {
    id: 'default-5',
    type: 'photo',
    title: 'Diversidad deportiva',
    description: 'Más allá del fútbol, todos los deportes',
    data_url:
      'https://images.unsplash.com/flagged/photo-1568407371446-a239664fb4f4?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
  },
  {
    id: 'default-6',
    type: 'photo',
    title: 'Foco y determinación',
    description: 'Cada día, un paso más cerca del sueño',
    data_url:
      'https://images.unsplash.com/photo-1609422644211-a85c36ee36a7?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
  },
];

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    getGallery()
      .then((res) => {
        const uploaded = res.data || [];
        // Show uploaded items first, then defaults to fill in
        const combined = [...uploaded, ...DEFAULT_GALLERY].slice(0, 12);
        setItems(combined);
      })
      .catch(() => setItems(DEFAULT_GALLERY));
  }, []);

  return (
    <section id="galeria" className="py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-sky-600 font-semibold text-sm uppercase tracking-wide mb-2 block">
            Galería
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Momentos que Inspiran
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Una mirada al trabajo diario de la fundación y los logros de nuestros atletas
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
          {items.map((item, idx) => (
            <button
              key={item.id || idx}
              data-testid={`gallery-item-${idx}`}
              onClick={() => setSelectedItem(item)}
              className="group relative aspect-square overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              {item.type === 'video' ? (
                <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                  {item.data_url && item.data_url.startsWith('data:') ? (
                    <video src={item.data_url} className="w-full h-full object-cover" muted />
                  ) : (
                    <div className="text-white">
                      <Play className="w-12 h-12" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-sky-600 ml-1 fill-sky-600" />
                    </div>
                  </div>
                </div>
              ) : (
                <img
                  src={item.data_url}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              )}

              <div className="absolute inset-0 bg-gradient-to-t from-sky-900/90 via-sky-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <div className="text-white text-left">
                  <p className="font-bold text-sm">{item.title}</p>
                  {item.description && (
                    <p className="text-xs text-sky-100 mt-1 line-clamp-2">{item.description}</p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedItem && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
          data-testid="gallery-lightbox"
        >
          <button
            data-testid="gallery-lightbox-close"
            onClick={() => setSelectedItem(null)}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-5xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {selectedItem.type === 'video' ? (
              <video
                src={selectedItem.data_url}
                controls
                autoPlay
                className="w-full max-h-[80vh] rounded-xl"
              />
            ) : (
              <img
                src={selectedItem.data_url}
                alt={selectedItem.title}
                className="w-full max-h-[80vh] object-contain rounded-xl"
              />
            )}
            <div className="text-white text-center mt-4">
              <p className="font-bold text-lg">{selectedItem.title}</p>
              {selectedItem.description && (
                <p className="text-sky-200 mt-1">{selectedItem.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
